import { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { getErrorMessage } from 'helpers/candidate_application/form-page-helpers';
import { filterUploadsForField } from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { textWithReferences } from 'helpers/form';
import { Controller, useWatch } from 'react-hook-form';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import { Checkbox } from 'components/FormComponents/Checkbox/Checkbox';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IdRequirementsList } from 'components/FormComponents/IdRequirementsList/IdRequirementsList';
import { Textarea } from 'components/FormComponents/Textarea/Textarea';
import { ValidationAlert } from 'components/FormComponents/ValidationAlert/ValidationAlert';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';

export const IdRequirementsWrapper = ({
  field = null,
  brand,
  uploadCode,
  className = '',
  getUpload,
  signUpload,
  createUpload,
  updateUpload,
  deleteUpload,
  modelParams,
  componentMap,
  control,
  setValue,
  trigger,
  getValues,
  register,
  allValues,
  errors = {},
  rejectedData,
  fieldPathIndex = {},
  name = 'id_requirements',
  textParams = {},
  uploads = [],
}) => {
  // Import getComponentProps directly to avoid circular dependency
  const { getComponentProps: originalGetComponentProps } = require('helpers/question-config');
  const {
    code,
    inclAltIdInstructions,
    altIdInstructionsCheckboxTitle,
    altIdInstructions,
    altIdFields,
    subFields,
    title,
    minPoints,
    requirementsMet,
    hasIdRequirements,
  } = field || {};

  const watchedValue = useWatch({
    control,
    name: `${code}_not_fulfilled`,
    defaultValue: allValues ? (allValues?.[`${code}_not_fulfilled`] ?? false) : false,
  });

  const watchedNotFulfilledReason = useWatch({
    control,
    name: `${code}_not_fulfilled_reason`,
    defaultValue: allValues ? (allValues?.[`${code}_not_fulfilled_reason`] ?? '') : '',
  });

  // Ensure currentFormValue is always a boolean (never undefined) to avoid controlled/uncontrolled warning
  const currentFormValue =
    watchedValue ?? (allValues ? (allValues?.[`${code}_not_fulfilled`] ?? false) : false);

  const [pointsMet, setPointsMet] = useState(0);
  const [pointsByComponent, setPointsByComponent] = useState({});

  const uploadHandlers = {
    getCandidateUpload: getUpload,
    signCandidateUpload: signUpload,
    createCandidateUpload: createUpload,
    updateCandidateUpload: updateUpload,
    deleteCandidateUpload: deleteUpload,
  };

  // Sync progress bar from field when sections are recalculated (e.g. ID assigned from another list).
  // This keeps the bar in sync with id-requirements.js without waiting for child callbacks.
  useEffect(() => {
    if (!minPoints || !subFields?.length) return;
    const nextByComponent = {};
    let total = 0;
    subFields.forEach((list, index) => {
      const listPoints = (list.options || []).reduce((sum, opt) => {
        if (!opt.selected || opt.needsComplementaryDoc) return sum;
        return sum + (Number.isFinite(opt.points) ? opt.points : 0);
      }, 0);
      nextByComponent[index] = listPoints;
      total += listPoints;
    });
    setPointsByComponent(nextByComponent);
    setPointsMet(total > minPoints ? minPoints : total);
  }, [minPoints, subFields]);

  // Handle points change from child components
  const handlePointsChange = useCallback(
    (componentIndex, newPoints) => {
      setPointsByComponent(prev => {
        const updated = { ...prev, [componentIndex]: newPoints };
        const total = Object.values(updated).reduce((sum, points) => sum + points, 0);
        const newPointsMet = total > minPoints ? minPoints : total;
        setPointsMet(newPointsMet);

        // selectedTypes will be updated by the useEffect when id_uploads change
        return updated;
      });
    },
    [minPoints],
  );

  const showIdLists = !(currentFormValue && altIdFields?.length > 0);
  const visibleSubFields = useMemo(() => {
    if (!subFields || !showIdLists) return [];
    return subFields
      .map((subField, index) => ({ subField, index }))
      .filter(({ subField }) => {
        if (!subField?.conditions) return true;
        return evaluateFieldConditions(subField.conditions, allValues || {}, { fieldPathIndex });
      });
  }, [subFields, showIdLists, allValues, fieldPathIndex]);
  const listsCollapsible = visibleSubFields.length > 1;

  return (
    <div className={cn('d-flex flex-column u-gap-medium')}>
      {minPoints && (
        <ProgressBar
          current={pointsMet}
          total={minPoints}
          label="Points counter"
          showLabel={true}
          showPercentage={true}
          size="small"
          variant="success"
          animated={true}
          description={`${minPoints} points required`}
        />
      )}

      {inclAltIdInstructions && (
        <div className="d-flex flex-column u-gap-medium">
          <Controller
            name={`${code}_not_fulfilled`}
            control={control}
            defaultValue={currentFormValue ?? false}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                name={`${code}_not_fulfilled`}
                checked={value ?? false}
                onChange={onChange}
                label={altIdInstructionsCheckboxTitle}
                register={register}
              />
            )}
          />
          {watchedValue && !altIdFields?.length && (
            <div className="d-flex flex-column u-gap-default">
              <span className={`primary-text u-font-size-small u-font-weight-500`}>
                Help us understand why you're not able to meet the ID requirements
              </span>
              <Textarea
                defaultValue={allValues?.[`${code}_not_fulfilled_reason`]}
                register={register}
                name={`${code}_not_fulfilled_reason`}
                validators={{ required: true }}
                placeholder="Please explain what's preventing you from providing the required ID or documents…"
                inputProps={{ rows: 3, maxLength: 1000 }}
              />
              {!watchedNotFulfilledReason && (
                <ValidationAlert
                  show={true}
                  message="This field is required and must be 1000 characters or less."
                />
              )}
            </div>
          )}
          {currentFormValue &&
            (altIdFields?.length > 0 ? (
              altIdFields.map((fieldGroup, groupIndex) => (
                <div key={groupIndex}>
                  <div className="d-flex flex-column u-gap-medium u-border u-padding u-border-radius">
                    <div className="d-flex flex-column u-gap-small">
                      {fieldGroup?.title != null && fieldGroup.title !== '' && (
                        <span
                          className="primary-text u-font-size-large u-font-weight-500"
                          dangerouslySetInnerHTML={{
                            __html: textWithReferences(fieldGroup.title, textParams) ?? '',
                          }}
                        />
                      )}
                      {fieldGroup?.description != null && fieldGroup.description !== '' && (
                        <span
                          className="secondary-text u-font-size-small u-font-weight-400 white-space-pre-line"
                          dangerouslySetInnerHTML={{
                            __html: textWithReferences(fieldGroup.description, textParams) ?? '',
                          }}
                        />
                      )}
                    </div>
                    {fieldGroup?.subFields?.length > 0 ? (
                      fieldGroup.subFields.map((subField, subFieldIndex) => {
                        let conditionsMet = true;
                        if (subField?.conditions) {
                          conditionsMet = evaluateFieldConditions(
                            subField.conditions,
                            allValues || {},
                            { fieldPathIndex },
                          );
                        }
                        if (!conditionsMet) return null;
                        return (
                          <QuestionWrapper
                            key={subField?.code || `${groupIndex}-${subFieldIndex}`}
                            question={subField}
                            componentMap={componentMap}
                            questionIndex={subFieldIndex}
                            control={control}
                            register={register}
                            setValue={setValue}
                            trigger={trigger}
                            getValues={getValues}
                            error={getErrorMessage(errors[subField.code])}
                            allValues={allValues}
                            errors={errors}
                            fieldPathIndex={fieldPathIndex}
                            textParams={textParams}
                            requirementVariantStyle="text"
                            gap="small"
                            titleSize="small"
                            fieldName={subField.code}
                            isVisible={conditionsMet}
                            getComponentProps={value => {
                              const filteredUploads = filterUploadsForField(uploads, subField.code);
                              return originalGetComponentProps({
                                question: subField,
                                value,
                                candidateId: uploadCode,
                                handlers: uploadHandlers,
                                brand,
                                fieldPathIndex,
                                allValues: allValues || {},
                                uploads: filteredUploads,
                              });
                            }}
                          />
                        );
                      })
                    ) : (
                      <FormMessage
                        message="Our support team will be in touch with you shortly to provide further instructions."
                        type={MessageType.Warning}
                        closeable={false}
                        noMargin
                      />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <FormMessage
                message={
                  altIdInstructions ||
                  'Our support team will be in touch with you shortly to provide further instructions.'
                }
                type={MessageType.Warning}
                closeable={false}
                noMargin
              />
            ))}
        </div>
      )}

      <div
        className={cn('d-flex flex-column u-gap-medium justify-content-center align-items-center', {
          [className]: className,
        })}>
        <div className="u-width-100 d-flex flex-column u-gap-medium">
          {visibleSubFields.map(({ subField, index }) => (
            <IdRequirementsList
              key={subField?.code || index}
              code={subField?.code}
              uploadCode={uploadCode}
              brand={brand}
              title={subField?.title}
              description={subField?.description}
              options={subField.options}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              updateUpload={updateUpload}
              deleteUpload={deleteUpload}
              modelParams={modelParams}
              hasIdRequirements={subField?.hasIdRequirements}
              min={subField?.min}
              requirementsMet={subField?.requirementsMet}
              wrapperRequirementsMet={requirementsMet}
              collapsible={listsCollapsible}
              onPointsChange={points => handlePointsChange(index, points)}
              setValue={setValue}
              rejectedData={rejectedData}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
